import { useTheme } from '@/theme';
import { IconButton, Text } from '@/components/ui';
import type { Table } from '@tanstack/react-table';

interface PaginationProps<TData> {
  table: Table<TData>;
  pageSizeOptions?: number[];
}

export function Pagination<TData>({ table, pageSizeOptions = [10, 20, 50, 100] }: PaginationProps<TData>) {
  const { theme } = useTheme();

  return (
    <div
      style={{
        display: 'flex',
        paddingTop: `${theme.spacing[4]}px`,
        justifyContent: 'space-between',
        alignItems: 'center',
        alignSelf: 'stretch',
      }}
    >
      {/* 왼쪽: 페이지 정보 + 셀렉트 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: `${theme.spacing[4]}px`,
        }}
      >
        <Text size="sm" color="textSecondary">
          Rows Per Page
        </Text>
        <select
          value={table.getState().pagination.pageSize}
          onChange={e => {
            table.setPageSize(Number(e.target.value));
          }}
          style={{
            padding: `${theme.spacing[2]}px ${theme.spacing[3]}px`,
            borderRadius: `${theme.borderRadius.sm}px`,
            border: `1px solid ${theme.colors.border.container}`,
            backgroundColor: theme.colors.surface.layout.overlay,
            color: theme.colors.text.primary,
            fontSize: `${theme.fontSize.xs}px`,
            cursor: 'pointer',
          }}
        >
          {pageSizeOptions.map(size => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>

      {/* 오른쪽: 페이지 번호 + 페이지네이션 버튼들 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: `${theme.spacing[4]}px`,
        }}
      >
        <IconButton
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
          aria-label="First page"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: theme.colors.icon.primary }}>
            <path d="M15 16L11.4 12L15 8M9 8V16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </IconButton>
        <IconButton
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          aria-label="Previous page"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: theme.colors.icon.primary }}>
            <path d="M14 16L10 12L14 8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </IconButton>
        <Text style={{ whiteSpace: 'nowrap', padding: `0 ${theme.spacing[2]}px` }}>
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </Text>
        <IconButton
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          aria-label="Next page"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: theme.colors.icon.primary }}>
            <path d="M10 16L14 12L10 8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </IconButton>
        <IconButton
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          disabled={!table.getCanNextPage()}
          aria-label="Last page"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: theme.colors.icon.primary }}>
            <path d="M9 16L12.6 12L9 8M15 8V16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </IconButton>
      </div>
    </div>
  );
}
